import React from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * TableField — a repeating editable grid.
 *
 * Data shape stored in `answers[field_id]`:
 *   [ { column_id: value, column_id: value, ... }, ... ]
 *
 * Rows are typed by their column's `type` (short_text | number | currency |
 * percent | date | url). We render one <input> per cell to keep the DOM flat
 * and keyboard-navigable. min_rows/max_rows come from the field definition.
 */
function TableCell({ column, value, onChange, ariaLabel }) {
  const type = column.type || "short_text";
  const common = {
    className:
      "w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70 focus:ring-1 focus:ring-[color:var(--brand-accent,#E91E63)]/40",
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    "aria-label": ariaLabel,
    "data-testid": `${column.column_id}-cell`,
  };

  if (type === "number" || type === "percent") {
    return (
      <div className="relative">
        <input type="number" step="any" {...common} />
        {type === "percent" && (
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
        )}
      </div>
    );
  }
  if (type === "currency") {
    return (
      <div className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
        <input type="number" step="any" {...common} className={`${common.className} pl-6`} />
      </div>
    );
  }
  if (type === "date") return <input type="date" {...common} />;
  if (type === "url")  return <input type="url" placeholder="https://…" {...common} />;
  // Fallback: short text (also used for `single_select`-esque free cells).
  return <input type="text" {...common} />;
}

export default function TableField({ field, value, onChange }) {
  // Value must always be an array. If the FE hasn't materialised min_rows yet,
  // do it once so the user sees the empty grid the spec asks for.
  const columns = field.columns || [];
  const minRows = Math.max(field.min_rows || 1, 1);
  const rows = Array.isArray(value) && value.length >= minRows
    ? value
    : [...(Array.isArray(value) ? value : []),
       ...Array.from({ length: minRows - (Array.isArray(value) ? value.length : 0) },
         () => Object.fromEntries(columns.map((c) => [c.column_id, ""])))];

  const setRows = (nextRows) => onChange(nextRows);

  const updateCell = (rowIdx, columnId, cellValue) => {
    const next = rows.map((r, i) =>
      i === rowIdx ? { ...r, [columnId]: cellValue } : r);
    setRows(next);
  };

  const addRow = () => {
    const empty = Object.fromEntries(columns.map((c) => [c.column_id, ""]));
    setRows([...rows, empty]);
  };

  const removeRow = (idx) => {
    if (rows.length <= minRows) return;
    setRows(rows.filter((_, i) => i !== idx));
  };

  const rowLabel = field.row_label || "Row";
  const maxRows = field.max_rows || 0; // 0 means unlimited

  return (
    <div
      className="rounded-lg border border-border bg-white overflow-x-auto"
      data-testid={`table-field-${field.field_id}`}
    >
      <table className="w-full text-sm min-w-[720px]">
        <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
          <tr>
            <th className="px-3 py-2 w-10">#</th>
            {columns.map((c) => (
              <th key={c.column_id} className="px-3 py-2 font-medium">
                {c.label}
              </th>
            ))}
            <th className="px-2 py-2 w-8" aria-label="Actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, idx) => (
            <tr key={idx} className="align-top">
              <td className="px-3 py-2 text-muted-foreground text-xs">{idx + 1}</td>
              {columns.map((c) => (
                <td key={c.column_id} className="px-3 py-2 min-w-[140px]">
                  <TableCell
                    column={c}
                    value={row?.[c.column_id]}
                    onChange={(v) => updateCell(idx, c.column_id, v)}
                    ariaLabel={`${rowLabel} ${idx + 1} — ${c.label}`}
                  />
                </td>
              ))}
              <td className="px-2 py-2">
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={rows.length <= minRows}
                  className="p-1 rounded hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={`Remove ${rowLabel} ${idx + 1}`}
                  data-testid={`remove-row-${field.field_id}-${idx}`}
                >
                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs">
        <span className="text-muted-foreground">
          {rows.length} {rows.length === 1 ? rowLabel : `${rowLabel}s`}
          {minRows > 1 && <> · min {minRows}</>}
        </span>
        <button
          type="button"
          onClick={addRow}
          disabled={maxRows > 0 && rows.length >= maxRows}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border hover:border-foreground/40 text-xs disabled:opacity-30"
          data-testid={`add-row-${field.field_id}`}
        >
          <Plus className="w-3.5 h-3.5" /> Add {rowLabel.toLowerCase()}
        </button>
      </div>
    </div>
  );
}
